import { useEffect, useState, useMemo, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  StickyNote, Inbox, Send, RotateCcw, CheckSquare,
  Clock, Palette, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

// Realistic sticky note palette — solid pastels with a darker "edge" tone for the
// top-strip tape effect. Keys must match the `color` field stored in the DB.
const COLOR_CLASSES = {
  yellow: { bg: '#fde047', edge: 'rgba(120, 80, 0, 0.08)',  text: 'text-yellow-950',  accent: 'text-yellow-900/70',  placeholder: 'placeholder:text-yellow-950/55' },
  pink:   { bg: '#f9a8d4', edge: 'rgba(150, 30, 80, 0.10)', text: 'text-pink-950',    accent: 'text-pink-900/70',    placeholder: 'placeholder:text-pink-950/55' },
  blue:   { bg: '#7dd3fc', edge: 'rgba(20, 60, 150, 0.10)', text: 'text-sky-950',     accent: 'text-sky-900/70',     placeholder: 'placeholder:text-sky-950/55' },
  green:  { bg: '#86efac', edge: 'rgba(20, 100, 50, 0.10)', text: 'text-emerald-950', accent: 'text-emerald-900/70', placeholder: 'placeholder:text-emerald-950/55' },
  purple: { bg: '#c4b5fd', edge: 'rgba(70, 40, 150, 0.10)', text: 'text-purple-950',  accent: 'text-purple-900/70',  placeholder: 'placeholder:text-purple-950/55' },
  orange: { bg: '#fdba74', edge: 'rgba(150, 80, 20, 0.10)', text: 'text-orange-950',  accent: 'text-orange-900/70',  placeholder: 'placeholder:text-orange-950/55' },
};
const COLORS = Object.keys(COLOR_CLASSES);
const PLACEHOLDER = 'Quick reminder, idea, or note...';

// Per-slot deterministic tilt so notes look hand-stuck, not on rerender jitter.
const TILTS_BY_SLOT = { 1: -1.5, 2: 1, 3: -0.5, 4: 2, 5: -2, 6: 0.8 };

function formatRemaining(seconds) {
  if (seconds == null) return '';
  if (seconds <= 0) return 'expiring...';
  if (seconds < 3600) return `${Math.ceil(seconds / 60)}m left`;
  if (seconds < 86400) return `${Math.ceil(seconds / 3600)}h left`;
  return `${Math.ceil(seconds / 86400)}d left`;
}

/**
 * Blue pad of fresh Sticky Notes. Click to write a new one.
 * `size` controls the rendered dimensions; the SVG shows a top Sticky Note
 * sitting on a visible stack of paper edges so it reads unmistakably as
 * "a pad of Sticky Notes" — top sheet with tape strip and ruled lines,
 * peeled corner indicating it's the next one to grab.
 */
function StickyPad({ size = 'lg', onClick, hint }) {
  // Big pad sized 10% smaller than v5 per user feedback (h-48 → h-44, w-40 → w-36)
  const dim = size === 'lg' ? 'h-44 w-36' : 'h-24 w-20';
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col items-center gap-3 outline-none transition"
      data-testid={`sticky-pad-${size}`}
    >
      <div className={`relative ${dim} transition-transform group-hover:-translate-y-1 group-hover:scale-[1.02]`}>
        <svg viewBox="0 0 120 150" className="w-full h-full drop-shadow-[0_6px_8px_rgba(0,0,0,0.25)]" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          {/* Stack edge: many thin horizontal lines representing dozens of stacked sheets */}
          <rect x="6" y="128" width="108" height="16" rx="1" fill="#3b82f6" stroke="#1e3a8a" strokeWidth="2" />
          <g stroke="#1e3a8a" strokeWidth="0.6" opacity="0.6">
            <line x1="8" y1="131" x2="112" y2="131" />
            <line x1="8" y1="134" x2="112" y2="134" />
            <line x1="8" y1="137" x2="112" y2="137" />
            <line x1="8" y1="140" x2="112" y2="140" />
          </g>

          {/* Top Sticky Note body */}
          <rect x="6" y="10" width="108" height="120" rx="2" fill="#bae6fd" stroke="#1e3a8a" strokeWidth="2.5" />

          {/* Tape strip at top (darker blue band) */}
          <rect x="6" y="10" width="108" height="14" fill="#38bdf8" stroke="#1e3a8a" strokeWidth="2.5" />

          {/* Ruled lines = ready-to-write surface */}
          <g stroke="#1e3a8a" strokeWidth="1.4" opacity="0.28" strokeLinecap="round">
            <line x1="18" y1="44" x2="102" y2="44" />
            <line x1="18" y1="60" x2="102" y2="60" />
            <line x1="18" y1="76" x2="102" y2="76" />
            <line x1="18" y1="92" x2="78" y2="92" />
          </g>

          {/* Peeled corner triangle — signals "next sheet ready to grab" */}
          <path d="M 100 130 L 114 130 L 114 116 Z" fill="#7dd3fc" stroke="#1e3a8a" strokeWidth="2" strokeLinejoin="round" />
        </svg>
      </div>
      {/* Hint text bumped 1pt larger per user feedback */}
      {hint && <span className="text-[12px] text-muted-foreground group-hover:text-foreground transition text-center max-w-[16rem]">{hint}</span>}
    </button>
  );
}

export function ClipboardModal({ open, onOpenChange, onInboxCountChange }) {
  const { user } = useAuth();
  const [state, setState] = useState({ board: [], stack: [], inbox: [], limits: null });
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ content: '', color: 'blue', reset_days: 7 });
  const [confirmConvert, setConfirmConvert] = useState(null);
  const [confirmInboxConvert, setConfirmInboxConvert] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ recipient_id: '', content: '', color: 'blue' });
  const [users, setUsers] = useState([]);
  const [inboxCounts, setInboxCounts] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get('/clipboard');
      setState(r.data);
      onInboxCountChange?.(r.data.inbox.filter(i => !i.is_read).length);
    } catch {
      toast.error('Could not load clipboard');
    }
  }, [onInboxCountChange]);

  
  useEffect(() => {
    if (!open) return;
    load();
    api.get('/users').then(r => setUsers((r.data || []).filter(u => u.is_active && u.id !== user?.id))).catch(() => {});
  }, [open, load, user?.id]);

  // Refresh teammate inbox counts each time the Send dialog opens
  useEffect(() => {
    if (!sendOpen) return;
    api.get('/clipboard/inbox-counts').then(r => setInboxCounts(r.data?.counts || {})).catch(() => {});
  }, [sendOpen]);
  

  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => {
      setState(prev => ({
        ...prev,
        board: prev.board.map(s => ({
          ...s,
          seconds_until_reset: s.seconds_until_reset != null ? Math.max(s.seconds_until_reset - 30, 0) : null,
        })),
      }));
    }, 30000);
    return () => clearInterval(t);
  }, [open]);

  const inboxUnread = useMemo(() => state.inbox.filter(i => !i.is_read).length, [state.inbox]);

  // The board is what the user sees as "their notes". The stack is the pool of
  // fresh blue Sticky Notes ready to be filled in.
  const boardNotes = useMemo(
    () => [...state.board].sort((a, b) => (a.slot || 0) - (b.slot || 0)),
    [state.board],
  );

  const openNewFromStack = () => {
    const fresh = state.stack[0];
    if (!fresh) return;
    setEditing(fresh);
    setEditForm({ content: '', color: 'blue', reset_days: 7 });
  };

  const openEditor = (sticky) => {
    setEditing(sticky);
    setEditForm({
      content: sticky.content || '',
      color: sticky.color || 'blue',
      reset_days: 7,
    });
  };

  const saveBoardEdit = async () => {
    try {
      await api.put(`/clipboard/${editing.id}`, {
        content: editForm.content,
        color: editForm.color,
      });
      toast.success('Sticky Note updated');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const placeOnBoard = async () => {
    if (!editForm.content.trim()) return toast.error('Add content first');
    try {
      await api.post(`/clipboard/${editing.id}/place`, {
        content: editForm.content,
        color: editForm.color,
        reset_days: editForm.reset_days,
      });
      toast.success('Pinned to board');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not place');
    }
  };

  const returnToStack = async (sid) => {
    try {
      await api.post(`/clipboard/${sid}/return`);
      toast.success('Returned to Notepad');
      load();
    } catch {
      toast.error('Failed');
    }
  };

  const convertToTodo = async () => {
    if (!confirmConvert) return;
    try {
      await api.post(`/clipboard/${confirmConvert.id}/convert-to-todo`, { clear_sticky: true });
      toast.success('Converted to To-Do');
      setConfirmConvert(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed');
    }
  };

  const convertInboxToTodo = async () => {
    if (!confirmInboxConvert) return;
    try {
      await api.post(`/clipboard/inbox/${confirmInboxConvert.id}/convert-to-todo`);
      toast.success('Converted to To-Do');
      setConfirmInboxConvert(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Convert failed');
    }
  };

  const sendSticky = async () => {
    if (!sendForm.recipient_id) return toast.error('Pick a recipient');
    if (!sendForm.content.trim()) return toast.error('Sticky Note content required');
    try {
      await api.post('/clipboard/send', sendForm);
      toast.success('Sticky Note sent');
      setSendForm({ recipient_id: '', content: '', color: 'blue' });
      setSendOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    }
  };

  const markInboxRead = async (iid) => {
    await api.put(`/clipboard/inbox/${iid}/read`).catch(() => {});
    setState(prev => {
      const next = { ...prev, inbox: prev.inbox.map(i => i.id === iid ? { ...i, is_read: true } : i) };
      onInboxCountChange?.(next.inbox.filter(i => !i.is_read).length);
      return next;
    });
  };

  const dismissInbox = async (iid) => {
    try {
      await api.delete(`/clipboard/inbox/${iid}`);
      setState(prev => {
        const next = { ...prev, inbox: prev.inbox.filter(i => i.id !== iid) };
        onInboxCountChange?.(next.inbox.filter(i => !i.is_read).length);
        return next;
      });
    } catch {
      toast.error('Dismiss failed');
    }
  };

  const boardCount = boardNotes.length;
  const stackCount = state.stack.length;

  return (
    <TooltipProvider delayDuration={200}>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-card border-border max-w-4xl p-0 gap-0 overflow-hidden" data-testid="clipboard-modal">
          <DialogHeader className="px-6 py-4 border-b border-border">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-md bg-amber-500/10 border border-amber-500/30">
                  <StickyNote className="h-4 w-4 text-amber-400" />
                </div>
                <div>
                  <DialogTitle className="text-base" data-testid="clipboard-title">Clipboard</DialogTitle>
                  <DialogDescription className="text-xs mt-0.5 leading-relaxed max-w-xl">
                    Your personal clipboard for short reminders and ideas. Pin them to your board using the Sticky Notes, leave a note on your teammate&apos;s board, or turn them into To-Dos.
                  </DialogDescription>
                </div>
              </div>
              <Button onClick={() => setSendOpen(true)} size="sm" variant="outline" data-testid="send-sticky-btn">
                <Send className="h-3.5 w-3.5 mr-1.5" /> Send to teammate
              </Button>
            </div>
          </DialogHeader>

          <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Main canvas: Board on top, Stack pad below (always rendered first
                so personal Sticky Notes lead the experience) */}
            <div
              className="relative rounded-lg border border-dashed border-border/80 bg-muted/[0.04] p-5 sm:p-6"
              data-testid="clipboard-canvas"
              style={{
                backgroundImage: 'radial-gradient(circle, hsl(var(--border) / 0.4) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            >
              {boardCount === 0 ? (
                /* Empty board: big blue stack pad as centerpiece */
                <div className="py-10 flex flex-col items-center gap-2" data-testid="clipboard-empty-state">
                  <StickyPad size="lg" onClick={openNewFromStack} hint="Click the pad to write your first Sticky Note." />
                  <p className="text-[12px] text-muted-foreground text-center max-w-sm mt-1">
                    You have 6 Sticky Notes left in your Notepad. Pin them to your board with a reset timer so they stay visible until you act on them.
                  </p>
                </div>
              ) : (
                <>
                  {/* Board grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                    {boardNotes.map(s => (
                      <StickyNoteCard
                        key={s.id}
                        sticky={s}
                        onEdit={() => openEditor(s)}
                        onReturn={() => returnToStack(s.id)}
                        onConvert={() => setConfirmConvert(s)}
                      />
                    ))}
                  </div>
                  {/* Stack pad below board (only when stack has slots remaining) */}
                  {stackCount > 0 && (
                    <div className="mt-6 pt-4 border-t border-dashed border-border/60 flex items-center justify-center gap-4">
                      <StickyPad
                        size="sm"
                        onClick={openNewFromStack}
                        hint={`${stackCount} Sticky Note${stackCount === 1 ? '' : 's'} left in your Notepad`}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Inbox (below the canvas so personal notes lead the view) */}
            {state.inbox.length > 0 && (
              <section data-testid="clipboard-inbox" className="space-y-2">
                <div className="flex items-center gap-2">
                  <Inbox className="h-3.5 w-3.5 text-amber-400" />
                  <h2 className="text-xs font-semibold">Inbox from teammates</h2>
                  {inboxUnread > 0 && (
                    <span className="text-xs text-amber-400">({inboxUnread} unread)</span>
                  )}
                </div>
                {/* Inbox stickies are intentionally smaller (w-44) than personal
                    board ones so they read as compact "passed notes" vs. the
                    full-size personal reminders above. */}
                <div className="flex flex-wrap gap-3">
                  {state.inbox.map(i => (
                    <InboxStickyNote
                      key={i.id}
                      inbox={i}
                      onRead={() => markInboxRead(i.id)}
                      onDismiss={() => dismissInbox(i.id)}
                      onConvert={() => setConfirmInboxConvert(i)}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>{editing?.location === 'board' ? 'Edit Sticky Note' : 'New Sticky Note'}</DialogTitle>
            <DialogDescription>
              {editing?.location === 'board'
                ? 'Update content or color. Reset timer stays as-is.'
                : 'Write your thought, pick a color, and pin it to the board with a reset timer.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div
                className="rounded-sm border p-3 shadow-md"
                style={{
                  backgroundColor: COLOR_CLASSES[editForm.color].bg,
                  borderColor: COLOR_CLASSES[editForm.color].edge,
                  backgroundImage: `linear-gradient(180deg, ${COLOR_CLASSES[editForm.color].edge} 0%, ${COLOR_CLASSES[editForm.color].edge} 14px, transparent 14px)`,
                }}
              >
                <Textarea
                  value={editForm.content}
                  onChange={e => setEditForm({ ...editForm, content: e.target.value.slice(0, state.limits?.content_max || 280) })}
                  rows={5}
                  placeholder={PLACEHOLDER}
                  className={`bg-transparent border-0 resize-none focus-visible:ring-0 text-[12px] font-medium ${COLOR_CLASSES[editForm.color].text} ${COLOR_CLASSES[editForm.color].placeholder}`}
                  data-testid="sticky-content-input"
                />
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <div className="flex items-center gap-1.5">
                  <Palette className="h-3 w-3 text-muted-foreground" />
                  {COLORS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, color: c })}
                      className={`h-5 w-5 rounded-full ${editForm.color === c ? 'ring-2 ring-offset-1 ring-offset-card ring-primary' : 'ring-1 ring-border'}`}
                      style={{ backgroundColor: COLOR_CLASSES[c].bg }}
                      data-testid={`sticky-color-${c}`}
                      aria-label={c}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{editForm.content.length}/{state.limits?.content_max || 280}</span>
              </div>
            </div>

            {editing?.location === 'stack' && (
              <div>
                <Label className="text-xs">Reset Timer</Label>
                <Select value={String(editForm.reset_days)} onValueChange={v => setEditForm({ ...editForm, reset_days: parseInt(v, 10) })}>
                  <SelectTrigger className="bg-background border-border" data-testid="sticky-reset-days">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {(state.limits?.allowed_reset_days || [1, 3, 7, 10]).map(d => (
                      <SelectItem key={d} value={String(d)} className="text-xs">
                        {d} day{d === 1 ? '' : 's'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">You&apos;ll get a bell ping 1 hour before reset. When the timer expires, the Sticky Note&apos;s content is cleared and it returns to your Notepad as an empty Sticky Note.</p>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
            {editing?.location === 'stack' && (
              <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={placeOnBoard} disabled={!editForm.content.trim()} data-testid="sticky-place-confirm-btn">
                Pin to board
              </Button>
            )}
            {editing?.location === 'board' && (
              <Button size="sm" className="bg-primary text-primary-foreground" onClick={saveBoardEdit} data-testid="sticky-save-btn">
                Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to To-Do confirm (personal) */}
      <AlertDialog open={!!confirmConvert} onOpenChange={(o) => !o && setConfirmConvert(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Convert Sticky Note to To-Do?</AlertDialogTitle>
            <AlertDialogDescription>
              Creates a tracked task assigned to you. The Sticky Note will be cleared and returned to your Notepad.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={convertToTodo} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="confirm-convert-todo">Convert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Convert inbox Sticky Note to To-Do confirm */}
      <AlertDialog open={!!confirmInboxConvert} onOpenChange={(o) => !o && setConfirmInboxConvert(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Convert teammate&apos;s Sticky Note to To-Do?</AlertDialogTitle>
            <AlertDialogDescription>
              Creates a tracked task assigned to you and dismisses the inbox Sticky Note from {confirmInboxConvert?.sender_name || 'your teammate'} so it frees up an inbox slot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={convertInboxToTodo} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="confirm-convert-inbox-todo">Convert</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send Dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Send a Sticky Note</DialogTitle>
            <DialogDescription>The recipient sees this in their Inbox. Max 3 active inbound Sticky Notes per teammate. 200 character limit.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Recipient</Label>
              <Select value={sendForm.recipient_id} onValueChange={v => setSendForm({ ...sendForm, recipient_id: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="send-recipient">
                  <SelectValue placeholder="Pick a teammate..." />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-72">
                  {users.map(u => {
                    const count = inboxCounts[u.id] || 0;
                    const isFull = count >= (state.limits?.inbox_max || 3);
                    return (
                      <SelectItem key={u.id} value={u.id} className="text-xs" disabled={isFull}>
                        <span className="flex items-center gap-2">
                          <span>{u.name}</span>
                          <span className={`text-xs ${isFull ? 'text-destructive' : 'text-muted-foreground'}`}>
                            ({count}/{state.limits?.inbox_max || 3}{isFull ? ' full' : ''})
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div
                className="rounded-sm border p-3 shadow-md"
                style={{
                  backgroundColor: COLOR_CLASSES[sendForm.color].bg,
                  borderColor: COLOR_CLASSES[sendForm.color].edge,
                  backgroundImage: `linear-gradient(180deg, ${COLOR_CLASSES[sendForm.color].edge} 0%, ${COLOR_CLASSES[sendForm.color].edge} 14px, transparent 14px)`,
                }}
              >
                <Textarea
                  value={sendForm.content}
                  onChange={e => setSendForm({ ...sendForm, content: e.target.value.slice(0, state.limits?.cross_user_content_max || 200) })}
                  rows={5}
                  placeholder={PLACEHOLDER}
                  className={`bg-transparent border-0 resize-none focus-visible:ring-0 text-[12px] font-medium ${COLOR_CLASSES[sendForm.color].text} ${COLOR_CLASSES[sendForm.color].placeholder}`}
                  data-testid="send-content"
                />
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <div className="flex items-center gap-1.5">
                  <Palette className="h-3 w-3 text-muted-foreground" />
                  {COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setSendForm({ ...sendForm, color: c })}
                      className={`h-5 w-5 rounded-full ${sendForm.color === c ? 'ring-2 ring-offset-1 ring-offset-card ring-primary' : 'ring-1 ring-border'}`}
                      style={{ backgroundColor: COLOR_CLASSES[c].bg }}
                      aria-label={c} />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">{sendForm.content.length}/{state.limits?.cross_user_content_max || 200}</span>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSendOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-primary text-primary-foreground" onClick={sendSticky} data-testid="send-sticky-confirm">
              <Send className="h-3.5 w-3.5 mr-1.5" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

function StickyNoteCard({ sticky, onEdit, onReturn, onConvert }) {
  const c = COLOR_CLASSES[sticky.color || 'blue'] || COLOR_CLASSES.blue;
  const tilt = TILTS_BY_SLOT[sticky.slot] ?? 0;
  return (
    <div
      onClick={onEdit}
      data-testid={`sticky-board-${sticky.id}`}
      className="group relative aspect-[3/4] rounded-sm p-4 cursor-pointer flex flex-col transition-transform duration-150 hover:!rotate-0 hover:scale-[1.04] hover:z-10"
      style={{
        backgroundColor: c.bg,
        // Top "tape" strip: subtle darker band, drawn via background gradient
        backgroundImage: `linear-gradient(180deg, ${c.edge} 0%, ${c.edge} 14px, transparent 14px)`,
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.18), 0 2px 4px rgba(0, 0, 0, 0.10)',
        transform: `rotate(${tilt}deg)`,
      }}
    >
      <p className={`text-sm font-medium leading-snug ${c.text} flex-1 whitespace-pre-wrap break-words line-clamp-[10] mt-1.5`}>
        {sticky.content}
      </p>
      <div className="flex items-center justify-between mt-2 gap-1">
        {sticky.seconds_until_reset != null ? (
          <div className={`flex items-center gap-1 text-xs ${c.accent}`}>
            <Clock className="h-2.5 w-2.5" />
            {formatRemaining(sticky.seconds_until_reset)}
          </div>
        ) : <span />}
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {onConvert && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onConvert(); }}
                  className={`p-1 rounded ${c.text} hover:bg-black/10`}
                  data-testid={`sticky-convert-${sticky.id}`}
                  aria-label="Convert to To-Do"
                >
                  <CheckSquare className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Convert to To-Do.</TooltipContent>
            </Tooltip>
          )}
          {onReturn && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onReturn(); }}
                  className={`p-1 rounded ${c.text} hover:bg-black/10`}
                  data-testid={`sticky-return-${sticky.id}`}
                  aria-label="Clear content from Sticky Note and return to Notepad"
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Clear content from Sticky Note and return to Notepad.</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

function InboxStickyNote({ inbox, onRead, onDismiss, onConvert }) {
  const c = COLOR_CLASSES[inbox.color || 'blue'] || COLOR_CLASSES.blue;
  return (
    <div
      onClick={() => !inbox.is_read && onRead()}
      data-testid={`inbox-sticky-${inbox.id}`}
      className={`group relative w-44 aspect-[3/4] rounded-sm p-3 cursor-pointer transition flex flex-col ${!inbox.is_read ? 'ring-2 ring-amber-400/60 ring-offset-2 ring-offset-card' : ''}`}
      style={{
        backgroundColor: c.bg,
        backgroundImage: `linear-gradient(180deg, ${c.edge} 0%, ${c.edge} 14px, transparent 14px)`,
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.16), 0 2px 3px rgba(0, 0, 0, 0.08)',
      }}
    >
      <div className="flex items-center justify-between mb-1 mt-0.5">
        <span className={`text-xs font-semibold truncate ${c.accent}`}>From {inbox.sender_name}</span>
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          {onConvert && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onConvert(); }}
                  className={`p-1 rounded ${c.text} hover:bg-black/10`}
                  data-testid={`inbox-convert-${inbox.id}`}
                  aria-label="Convert to To-Do"
                >
                  <CheckSquare className="h-3 w-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">Convert to To-Do.</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                className={`p-1 rounded ${c.text} hover:bg-black/10`}
                data-testid={`inbox-dismiss-${inbox.id}`}
                aria-label="Dismiss"
              >
                <X className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">Dismiss this Sticky Note.</TooltipContent>
          </Tooltip>
        </div>
      </div>
      <p className={`text-xs font-medium leading-snug ${c.text} flex-1 whitespace-pre-wrap break-words line-clamp-[8]`}>
        {inbox.content}
      </p>
      <div className={`flex items-center gap-1 text-xs ${c.accent} mt-1.5`}>
        <Clock className="h-2.5 w-2.5" />
        {new Date(inbox.sent_at).toLocaleDateString()}
      </div>
    </div>
  );
}
